// Pure axis-domain math for price charts, extracted so it is unit-testable —
// the chart component itself renders SVG and is not.

export interface AxisDomain {
  min: number;
  max: number;
}

// Edge breathing room added above and below the span, as a fraction of the span,
// so the line doesn't touch the top/bottom of the plot area.
export const AXIS_PADDING_FRACTION = 0.1;

// Minimum visible span, as a fraction of the price level (the series midpoint).
// This is the key to honest amplitude: a tiny real range is shown inside a band
// proportional to the price, so a 2% move reads as a 2% move instead of being
// zoomed to fill the chart. A genuine swing larger than this floor governs the
// span itself, so real volatility still fills the chart.
export const AXIS_MIN_SPAN_FRACTION = 0.25;

/**
 * Price-proportional min/max domain for a price series.
 *
 * The visible span is `max(dataRange, midpoint * AXIS_MIN_SPAN_FRACTION)`,
 * centred on the series midpoint, then padded at the edges. Consequences:
 *
 * - A small real range (e.g. $795–$800 on an ~$800 item) is floored to a band
 *   proportional to the price (~25% of $800), so the line wobbles gently rather
 *   than swinging edge-to-edge — it no longer looks volatile when it isn't.
 * - A large genuine swing (dataRange > the floor) governs the span, so real
 *   volatility still fills the chart.
 * - The domain is NOT zero-based, so the series doesn't hug the top of a 0-axis.
 * - A flat series gets a non-zero band (no divide-by-zero, line not pinned).
 *
 * `min` is clamped to 0 (prices are non-negative). Empty input → `{ min: 0,
 * max: 1 }` (callers already gate on `length < 2`).
 */
export function computePaddedDomain(values: number[]): AxisDomain {
  if (values.length === 0) return { min: 0, max: 1 };

  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const mid = (lo + hi) / 2;
  const dataRange = hi - lo;

  // Floor the span to a fraction of the price level so a tiny real range isn't
  // zoomed into a fake mountain; a genuine swing larger than the floor wins.
  const span = Math.max(dataRange, mid * AXIS_MIN_SPAN_FRACTION, 1);
  const half = span / 2 + span * AXIS_PADDING_FRACTION;

  return { min: Math.max(0, mid - half), max: mid + half };
}

// Plot-area geometry of the SVG chart: its total height and the top/bottom
// contentInset. The line/area occupy `height - insetTop - insetBottom`, NOT the
// full height — overlays drawn over the chart must use the same numbers.
export interface PlotGeometry {
  height: number;
  insetTop: number;
  insetBottom: number;
}

/**
 * Pixel Y for a price `value` under the SAME linear scale react-native-svg-charts
 * applies to the line: domain `[min, max]` maps onto the vertical range
 * `[height - insetBottom, insetTop]` (max at the top inset, min at the bottom).
 *
 * The tooltip-dot overlay must project through this so the dot lands ON the
 * rendered line. The drawable height is derived from the insets here —
 * hardcoding it (it was 120 while the real plot area is `160 - 20 - 40 = 100`)
 * stretched the mapping and sank the dot below the line toward lower values.
 */
export function projectValueToPlotY(
  value: number,
  domain: AxisDomain,
  geom: PlotGeometry,
): number {
  const range = domain.max - domain.min || 1;
  const plotHeight = geom.height - geom.insetTop - geom.insetBottom;
  return geom.insetTop + ((domain.max - value) / range) * plotHeight;
}
